import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Alert, Collapse, ToggleButton, ToggleButtonGroup, Tooltip,
} from '@mui/material';
import { environment } from '../../env/environment';
import logger from '../../utils/logger';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('superAdminToken')}`,
});

function formatValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

const ACTION_TEXT = {
  created: 'Created a new contact in GHL.',
  updated: 'Done.',
  unchanged: 'Nothing needed changing.',
};

const CHANGE_LABEL = {
  overwrite: { text: 'Different', color: '#e65100', bg: '#fff3e0' },
  ghl_only: { text: 'Only in GHL', color: '#1565c0', bg: '#e3f2fd' },
  add: { text: 'Only in our record', color: '#2e7d32', bg: '#e8f5e9' },
};

// Most consequential first: values that differ, then GHL-only, then additions.
const ROW_ORDER = { overwrite: 0, ghl_only: 1, add: 2 };

/** Starting choice for each row: keep what the admin already picked if it's still possible. */
function initialChoices(plan, previous = {}) {
  const choices = {};
  for (const c of plan.changes) {
    if (c.change === 'same') continue;
    const kept = previous[c.field];
    const stillAllowed = kept === 'skip' || (kept === 'push' && c.canPush) || (kept === 'pull' && c.canPull);
    choices[c.field] = stillAllowed ? kept : c.defaultChoice;
  }
  return choices;
}

/**
 * Compares a patient with the GHL contact that has the same email and lets the admin
 * decide, per field, whether to push our value to GHL, pull GHL's value into our
 * record, or leave both alone.
 */
export default function GhlSyncDialog({ open, patient, tenantId, primaryColor, onClose }) {
  const [plan, setPlan] = useState(null);
  const [choices, setChoices] = useState({});
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [showUnchanged, setShowUnchanged] = useState(false);

  const loadPreview = useCallback(async () => {
    if (!patient || !tenantId) return;
    setLoading(true);
    setError(null);
    setPlan(null);
    setResult(null);
    try {
      const res = await fetch(`${environment.baseUrl}/api/admin/ghl-sync/${patient.submission_id}/preview`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({ tenantId }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Could not load the GHL preview');
      setPlan(data.plan);
      setChoices(initialChoices(data.plan));
    } catch (err) {
      logger.error('GHL sync preview failed:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [patient, tenantId]);

  useEffect(() => {
    if (open) loadPreview();
  }, [open, loadPreview]);

  const handleApply = async () => {
    setApplying(true);
    setError(null);
    try {
      const res = await fetch(`${environment.baseUrl}/api/admin/ghl-sync/${patient.submission_id}/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({ tenantId, fingerprint: plan.fingerprint, selections: choices }),
      });
      const data = await res.json();
      if (res.status === 409 && data.plan) {
        // GHL or our record changed since the preview: show the new differences and ask again.
        setPlan(data.plan);
        setChoices(prev => initialChoices(data.plan, prev));
        setError(data.message);
        return;
      }
      if (!data.success) throw new Error(data.message || 'Sync failed');
      setResult(data);
    } catch (err) {
      logger.error('GHL sync apply failed:', err);
      setError(err.message);
    } finally {
      setApplying(false);
    }
  };

  const changes = plan?.changes || [];
  const differences = changes
    .filter(c => c.change !== 'same')
    .sort((a, b) => ROW_ORDER[a.change] - ROW_ORDER[b.change]);
  const unchanged = changes.filter(c => c.change === 'same');
  const tagsToAdd = plan?.tags?.add || [];
  const pushCount = differences.filter(c => choices[c.field] === 'push').length;
  const pullCount = differences.filter(c => choices[c.field] === 'pull').length;
  const overwriteCount = differences.filter(c => choices[c.field] === 'push' && c.change === 'overwrite').length;
  const isCreate = plan?.action === 'create';
  const nothingChosen = isCreate ? pushCount === 0 : pushCount + pullCount + tagsToAdd.length === 0;
  const canApply = plan && !result && !applying && !nothingChosen;

  const setAll = (choice) => setChoices(prev => {
    const next = { ...prev };
    for (const c of differences) {
      if (choice === 'skip' || (choice === 'push' && c.canPush) || (choice === 'pull' && c.canPull)) next[c.field] = choice;
    }
    return next;
  });

  return (
    <Dialog open={open} onClose={() => !applying && onClose(!!result)} maxWidth="lg" fullWidth>
      <DialogTitle>
        Sync with GHL
        {patient && (
          <Typography variant="body2" color="text.secondary">
            {patient.patient_last_name}, {patient.patient_first_name} · {patient.patient_email}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
            <CircularProgress size={22} sx={{ color: primaryColor }} />
            <Typography variant="body2">Comparing with GHL…</Typography>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {result && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {ACTION_TEXT[result.action] || 'Done.'}
            {result.pushed?.length > 0 && ` Sent to GHL: ${result.pushed.join(', ')}.`}
            {result.pulled?.length > 0 && ` Updated our record from GHL: ${result.pulled.join(', ')}.`}
            {result.tags?.add?.length > 0 && ` Tagged ${result.tags.add.join(', ')}.`}
            {result.sharedPhone && ' The phone number belongs to another contact, so it was left out of the phone field and the contact was tagged emaildiff-numsame.'}
          </Alert>
        )}

        {plan && !result && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2">
              {isCreate
                ? 'No GHL contact has this email. A new contact will be created with the fields you push.'
                : 'Matched the GHL contact with this email. Choose what happens to each difference.'}
            </Typography>

            {plan.warnings.map(w => (
              <Alert key={w.type} severity="warning">{w.message}</Alert>
            ))}

            {differences.length === 0 && tagsToAdd.length === 0 && (
              <Alert severity="info">Our record and GHL already match. There is nothing to sync.</Alert>
            )}

            {differences.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mr: 'auto' }}>
                    {differences.length === 1 ? '1 difference' : `${differences.length} differences`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Set all:</Typography>
                  <Button size="small" onClick={() => setAll('push')} sx={{ textTransform: 'none' }}>Push</Button>
                  <Button size="small" onClick={() => setAll('skip')} sx={{ textTransform: 'none' }}>Skip</Button>
                  {!isCreate && <Button size="small" onClick={() => setAll('pull')} sx={{ textTransform: 'none' }}>Pull</Button>}
                </Box>
                <DifferenceTable
                  rows={differences}
                  choices={choices}
                  onChoose={(field, choice) => choice && setChoices(prev => ({ ...prev, [field]: choice }))}
                />
              </Box>
            )}

            {tagsToAdd.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Tags added to GHL</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {tagsToAdd.map(tag => (
                    <Chip key={tag} size="small" label={`+ ${tag}`} sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Uses the same tags live intake sets. Tags are only added, never removed, and workflow-trigger tags
                  (10x-create-contact, intake_final_submission) are never set.
                </Typography>
              </Box>
            )}

            {unchanged.length > 0 && (
              <Box>
                <Button size="small" onClick={() => setShowUnchanged(s => !s)} sx={{ textTransform: 'none', px: 0 }}>
                  {showUnchanged ? 'Hide' : 'Show'} {unchanged.length} matching fields
                </Button>
                <Collapse in={showUnchanged} unmountOnExit>
                  <MatchingTable rows={unchanged} />
                </Collapse>
              </Box>
            )}

            {(overwriteCount > 0 || pullCount > 0) && (
              <Alert severity="warning">
                {[
                  overwriteCount > 0 && `${overwriteCount} value${overwriteCount === 1 ? '' : 's'} in GHL will be overwritten`,
                  pullCount > 0 && `${pullCount} value${pullCount === 1 ? '' : 's'} in this patient's record here will be replaced with GHL's`,
                ].filter(Boolean).join(', and ')}.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {plan && !result && (
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>
            Push {pushCount} · Pull {pullCount}
          </Typography>
        )}
        <Button onClick={() => onClose(!!result)} disabled={applying}>{result ? 'Close' : 'Cancel'}</Button>
        {!result && (
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={!canApply}
            startIcon={applying ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
            sx={{ bgcolor: primaryColor, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: primaryColor } }}
          >
            {applying ? 'Applying…' : isCreate ? 'Create in GHL' : 'Apply'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function DifferenceTable({ rows, choices, onChoose }) {
  return (
    <Table size="small" sx={{ '& td, & th': { fontSize: 13, verticalAlign: 'middle' } }}>
      <TableHead>
        <TableRow sx={{ backgroundColor: '#f8fafb' }}>
          <TableCell sx={{ fontWeight: 600, width: '22%' }}>Field</TableCell>
          <TableCell sx={{ fontWeight: 600, width: '24%' }}>Our record</TableCell>
          <TableCell sx={{ fontWeight: 600, width: '24%' }}>GHL</TableCell>
          <TableCell sx={{ fontWeight: 600 }} align="right">Action</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(row => {
          const badge = CHANGE_LABEL[row.change];
          const choice = choices[row.field];
          return (
            <TableRow key={row.field}>
              <TableCell>
                <Box>{row.label}</Box>
                <Chip size="small" label={badge.text} sx={{ mt: 0.5, height: 18, fontSize: 10, bgcolor: badge.bg, color: badge.color, fontWeight: 600 }} />
              </TableCell>
              <TableCell sx={{ wordBreak: 'break-word', fontWeight: choice === 'push' ? 600 : 400 }}>{formatValue(row.proposed)}</TableCell>
              <TableCell sx={{ wordBreak: 'break-word', fontWeight: choice === 'pull' ? 600 : 400 }}>{formatValue(row.current)}</TableCell>
              <TableCell align="right">
                <ToggleButtonGroup size="small" exclusive value={choice} onChange={(_, value) => onChoose(row.field, value)}>
                  {row.canPush && (
                    <ToggleButton value="push" sx={{ textTransform: 'none', px: 1 }}>
                      <Tooltip title="Send our value to GHL"><span>Push →</span></Tooltip>
                    </ToggleButton>
                  )}
                  <ToggleButton value="skip" sx={{ textTransform: 'none', px: 1 }}>
                    <Tooltip title="Leave both as they are"><span>Skip</span></Tooltip>
                  </ToggleButton>
                  {row.canPull && (
                    <ToggleButton value="pull" sx={{ textTransform: 'none', px: 1 }}>
                      <Tooltip title="Replace our record's value with GHL's"><span>← Pull</span></Tooltip>
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function MatchingTable({ rows }) {
  return (
    <Table size="small" sx={{ '& td, & th': { fontSize: 13 } }}>
      <TableBody>
        {rows.map(row => (
          <TableRow key={row.field}>
            <TableCell sx={{ width: '22%' }}>{row.label}</TableCell>
            <TableCell sx={{ wordBreak: 'break-word' }}>{formatValue(row.proposed)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
